import PropTypes from 'prop-types';

import useTooltip from "../../../hooks/useTooltip";
import useAsyncButtonAction from "../../../hooks/useAsyncButtonAction";

import { WHITE } from '../../../utils/colours';

import Tooltip from "../../tooltip";
import Spinner from '../../spinner';
import { IconButtonContainer } from "./IconButton.styles";

const IconButton = (props) => {
    if (props.asyncAction) {
        return (
            <AsyncActionButton {...props} />
        );
    } else {
        return (
            <IconButtonComponent {...props} />
        );
    }
};

const IconButtonComponent = ({
    children,
    description,
    loading,
    svgMaxWidth,
    svgMaxHeight,
    tooltipPlacement,
    tooltipFixedPositioning = false,
    disabled,
    backgroundColour = WHITE(25),
    defaultBackgroundColour,
    colour = WHITE(),
    stopPropagation = false,
    ...props
}) => {
    const [tooltipProps, triggerProps] = useTooltip({ placement: tooltipPlacement, disabled, children: description, maxWidth: props.maxTooltipWidth, showOnMobile: props.showTooltipOnMobile, fixedPositioning: tooltipFixedPositioning });

    return (
        <>
            <IconButtonContainer
                type="button"
                {...triggerProps}
                aria-label={description}
                $loading={loading}
                $svgMaxWidth={svgMaxWidth}
                $svgMaxHeight={svgMaxHeight}
                $backgroundColour={backgroundColour}
                $defaultBackgroundColour={defaultBackgroundColour}
                $colour={colour}
                disabled={disabled}
                {...props}
                onClick={loading ? undefined : (e) => {
                    if (stopPropagation) {
                        e.stopPropagation();
                    }
                    if (props.onClick) {
                        props.onClick(e);
                    }
                }}
            >
                {
                    loading ? (
                        <Spinner colour={colour}/>
                    ) : children
                }
            </IconButtonContainer>
            {
                description && (
                    <Tooltip
                        {...tooltipProps}
                    >
                        {description}
                    </Tooltip>
                )
            }
        </>
    )
};

const AsyncActionButton = ({ asyncAction, ...props }) => {
    const asyncActionProps = useAsyncButtonAction(asyncAction);

    return (
        <IconButtonComponent
            {...asyncActionProps}
            {...props}
        />
    );
}

IconButton.propTypes = {
    /** Description shown in tooltip. If this is a node to render, you should memoize it and you must provide an alternative `aria-label`. */
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    /** Value for button's `aria-label` attribute. Only needs specifying if `description` is not a string. */
    'aria-label': (props, propName, componentName) => {
        if (!props[propName] && typeof props.description !== 'string' && !props.disabled) {
            return new Error(
                `${componentName} must have an aria-label if "description" is not a string.`
            );
        }
    },
    /** Show loading state. */
    loading: PropTypes.bool,
    /** Function called on click. It takes one argument which is a callback that must be invoked when function has finished. Button will show loading state until callback invoked. Do not use `onClick` when using this prop. */
    asyncAction: PropTypes.func,
    /** Max width of SVG within button. Use this to override defaults for images that are particularly wide. */
    svgMaxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** Max height of SVG within button. Use this to override defaults for images that are particularly tall. */
    svgMaxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** `placement` to pass to useTooltip. */
    tooltipPlacement: PropTypes.oneOf(['auto', 'auto-start', 'auto-end', 'top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end', 'right', 'right-start', 'right-end', 'left', 'left-start', 'left-end']),
    /** `fixedPositioning` value to pass to useTooltip. */
    tooltipFixedPositioning: PropTypes.bool,
    /** Colour of background circle when in focus or hover. */
    backgroundColour: PropTypes.string
};

export default IconButton;
